import { Logger } from '@uploadcam/common';
import pLimit from 'p-limit';
import { MirrorFeedItemInteractor, MirrorFeedItemResult } from './mirror-feed-item.interactor';

/** Default in-flight concurrency. Tuned for visible per-item progress without saturating the session. */
const DEFAULT_CONCURRENCY = 2;
/** Consecutive same-code failures before pausing the loop. Same code = systemic problem, not a one-off. */
const CIRCUIT_BREAKER_THRESHOLD = 5;

export interface MirrorFailure {
  itemId: string;
  /** Firebase Functions error code, e.g. `functions/unauthenticated`, `functions/failed-precondition`. */
  code: string;
  message: string;
  details?: unknown;
}

export type CircuitBreakDecision = 'retry' | 'cancel';

export interface MirrorGalleryItemsArgs {
  galleryId: string;
  itemIds: readonly string[];
  isCancelled?: () => boolean;
  /**
   * Called when N consecutive items have failed with the same error code. The caller can prompt
   * the user. Returning `'retry'` resets the streak counter and continues; `'cancel'` stops the
   * loop. If omitted, the loop just keeps going (failures accumulate in the summary).
   */
  onCircuitBreak?: (code: string, consecutive: number) => Promise<CircuitBreakDecision>;
  concurrency?: number;
}

export type MirrorGalleryItemsEvent =
  | { type: 'item-start'; itemId: string }
  | { type: 'item-done'; itemId: string; result: MirrorFeedItemResult }
  | { type: 'item-failed'; itemId: string; failure: MirrorFailure }
  | { type: 'circuit-break'; code: string; consecutive: number };

export interface MirrorGalleryItemsSummary {
  succeeded: string[];
  failed: MirrorFailure[];
  cancelled: string[];
}

export class MirrorGalleryItemsInteractor {
  constructor(
    private readonly logger: Logger,
    private readonly mirrorFeedItem: MirrorFeedItemInteractor,
  ) {}

  public async execute(
    args: MirrorGalleryItemsArgs,
    onEvent: (event: MirrorGalleryItemsEvent) => void,
  ): Promise<MirrorGalleryItemsSummary> {
    const summary: MirrorGalleryItemsSummary = { succeeded: [], failed: [], cancelled: [] };

    if (args.itemIds.length === 0) {
      return summary;
    }

    const limit = pLimit(args.concurrency ?? DEFAULT_CONCURRENCY);

    // Mutable streak tracker shared across in-flight items. Reset whenever an item succeeds
    // or whenever the caller decides to retry through the circuit-break callback.
    const streak = { code: '' as string, count: 0 };
    let cancelled = false;
    let circuitBreakInFlight: Promise<void> | null = null;

    const processOne = async (itemId: string): Promise<void> => {
      // The cancellation flag can flip while items are queued — bail before doing any work.
      if (cancelled || args.isCancelled?.()) {
        summary.cancelled.push(itemId);
        return;
      }

      // If a circuit-break dialog is open, wait for the user's decision before scheduling
      // any further work. Once the promise resolves, either we continue (streak reset) or
      // `cancelled` has been set and the next early-return catches it.
      if (circuitBreakInFlight) {
        await circuitBreakInFlight;
        if (cancelled) {
          summary.cancelled.push(itemId);
          return;
        }
      }

      onEvent({ type: 'item-start', itemId });

      try {
        const result = await this.mirrorFeedItem.execute({ galleryId: args.galleryId, itemId });
        streak.count = 0;
        streak.code = '';
        summary.succeeded.push(itemId);
        onEvent({ type: 'item-done', itemId, result });
      } catch (err) {
        const failure = toMirrorFailure(itemId, err);
        this.logger.error('mirrorFeedItem failed', failure);
        summary.failed.push(failure);
        onEvent({ type: 'item-failed', itemId, failure });

        if (failure.code === streak.code) {
          streak.count += 1;
        } else {
          streak.code = failure.code;
          streak.count = 1;
        }

        if (streak.count >= CIRCUIT_BREAKER_THRESHOLD && args.onCircuitBreak) {
          onEvent({ type: 'circuit-break', code: failure.code, consecutive: streak.count });
          // Single in-flight prompt at a time — other workers waiting on the same flag
          // resume once the user has chosen.
          if (!circuitBreakInFlight) {
            circuitBreakInFlight = (async () => {
              try {
                const decision = await args.onCircuitBreak!(failure.code, streak.count);
                if (decision === 'cancel') {
                  cancelled = true;
                } else {
                  streak.code = '';
                  streak.count = 0;
                }
              } finally {
                circuitBreakInFlight = null;
              }
            })();
          }
        }
      }
    };

    await Promise.all(args.itemIds.map((itemId) => limit(() => processOne(itemId))));
    return summary;
  }
}

function toMirrorFailure(itemId: string, err: unknown): MirrorFailure {
  const anyErr = err as { code?: string; message?: string; details?: unknown };
  return {
    itemId,
    code: anyErr.code ?? 'functions/unknown',
    message: anyErr.message ?? String(err),
    details: anyErr.details,
  };
}
